import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { formatMoney, resolveCurrencyCode } from '@/lib/money'
import * as categoryService from '../../services/categoryService'
import * as productService from '../../services/productService'
import { Card, CardHeader } from '../../components/ui/Card'
import { SkeletonListPage } from '../../components/ui/Skeleton'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { EmptyState } from '../../components/ui/EmptyState'
import { LoadMoreFooter } from '../../components/ui/LoadMoreFooter'
import { ProductBulkActionsBar } from '../../components/products/ProductBulkActionsBar'
import { InlineNumberEdit } from '../../components/products/InlineNumberEdit'
import { StockLevelBadge } from '../../components/products/StockLevelBadge'
import { useToast } from '../../context/ToastContext'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { useLoadMoreList } from '../../hooks/useLoadMoreList'
import { DEFAULT_LOW_STOCK_THRESHOLD, normalizeLowStockThreshold } from '../../lib/inventoryStock'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'
import {
  bulkAssignProductCategory,
  bulkDeleteProducts,
  bulkSetProductActive,
  bulkUpdateProductPriceStock,
  formatBulkResultMessage,
  type BulkProductFailure,
} from '../../lib/bulkProductOperations'

const DEFAULT_LIMIT = 20

type StatusFilter = 'all' | 'published' | 'draft'

type ProductRow = {
  id: number
  name: string
  slug: string
  sku: string
  price: unknown
  currency?: unknown
  stockQuantity: number
  isActive: boolean
}

function rowFromItem(item: unknown): ProductRow | null {
  if (!item || typeof item !== 'object') return null
  const o = item as Record<string, unknown>
  const id = typeof o.id === 'number' ? o.id : Number(o.id)
  if (!Number.isFinite(id)) return null
  return {
    id,
    name: String(o.name ?? ''),
    slug: String(o.slug ?? ''),
    sku: String(o.sku ?? ''),
    price: o.price,
    currency: o.currency,
    stockQuantity: typeof o.stockQuantity === 'number' ? o.stockQuantity : Number(o.stockQuantity ?? 0),
    isActive: o.isActive !== false,
  }
}

export default function ProductList() {
  const { pushToast } = useToast()
  const { storeRecord } = useCurrentStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialCategorySlug = searchParams.get('categorySlug')?.trim() ?? ''
  const lowStockFilter = searchParams.get('lowStock') === '1'

  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [categorySlug, setCategorySlug] = useState(initialCategorySlug)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [lowStockThreshold, setLowStockThreshold] = useState(DEFAULT_LOW_STOCK_THRESHOLD)
  const [categoryRows, setCategoryRows] = useState<categoryService.CategoryRow[]>([])
  const skipQResetRef = useRef(true)

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkCategorySlug, setBulkCategorySlug] = useState('')
  const [bulkPrice, setBulkPrice] = useState('')
  const [bulkStock, setBulkStock] = useState('')
  const [bulkFailures, setBulkFailures] = useState<BulkProductFailure[]>([])
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [inlineSavingId, setInlineSavingId] = useState<number | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    if (skipQResetRef.current) {
      skipQResetRef.current = false
      return
    }
  }, [q])

  const isActiveFilter =
    statusFilter === 'all' ? undefined : statusFilter === 'published' ? true : false

  const fetchPage = useCallback(
    async (page: number, limit: number) => {
      const res = await productService.fetchProducts({
        page,
        limit,
        q: q || undefined,
        categorySlug: categorySlug || undefined,
        isActive: isActiveFilter,
        ...(lowStockFilter ? { lowStock: true } : {}),
      })
      return {
        items: res.items.map(rowFromItem).filter((r): r is ProductRow => r != null),
        total: res.total,
      }
    },
    [q, categorySlug, isActiveFilter, lowStockFilter],
  )

  const {
    items: rows,
    total: listTotal,
    loading: listLoading,
    loadingMore,
    error: listError,
    hasMore,
    reload,
    loadMore,
    patchItem,
  } = useLoadMoreList<ProductRow>({
    fetchPage,
    limit: DEFAULT_LIMIT,
    resetDeps: [q, categorySlug, statusFilter, lowStockFilter],
  })

  useEffect(() => {
    if (storeRecord) {
      setLowStockThreshold(normalizeLowStockThreshold(storeRecord.lowStockThreshold))
    }
  }, [storeRecord])

  useEffect(() => {
    setSelectedIds(new Set())
    setBulkFailures([])
  }, [q, categorySlug, statusFilter, lowStockFilter])

  useEffect(() => {
    let cancelled = false
    categoryService
      .fetchCategories({ page: 1, limit: 100 })
      .then((res) => {
        if (!cancelled) setCategoryRows(res.items ?? [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const selectedItems = useMemo(
    () =>
      rows
        .filter((row) => selectedIds.has(row.id))
        .map((row) => ({ id: row.id, name: row.name })),
    [rows, selectedIds],
  )

  const pageIds = useMemo(() => rows.map((row) => row.id), [rows])
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
  const someOnPageSelected = pageIds.some((id) => selectedIds.has(id))

  const toggleRow = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setBulkFailures([])
  }, [])

  const toggleAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) {
        pageIds.forEach((id) => next.delete(id))
      } else {
        pageIds.forEach((id) => next.add(id))
      }
      return next
    })
    setBulkFailures([])
  }, [allOnPageSelected, pageIds])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
    setBulkFailures([])
    setBulkPrice('')
    setBulkStock('')
  }, [])

  async function saveInlineField(id: number, field: 'price' | 'stockQuantity', value: number) {
    setInlineSavingId(id)
    try {
      await productService.updateProduct(id, { [field]: value })
      patchItem(
        (row) => row.id === id,
        (row) => ({
          ...row,
          ...(field === 'price' ? { price: value } : { stockQuantity: value }),
        }),
      )
      pushToast({ message: 'Product saved', variant: 'success' })
    } catch (e) {
      pushToast({
        message: getUserFacingErrorMessage(e, 'Could not save product'),
        variant: 'error',
      })
      throw e
    } finally {
      setInlineSavingId(null)
    }
  }

  function applyBulkPriceStock() {
    const priceTrim = bulkPrice.trim()
    const stockTrim = bulkStock.trim()
    if (!priceTrim && !stockTrim) {
      pushToast({ message: 'Enter a price and/or stock quantity to apply.', variant: 'error' })
      return
    }

    const updates: { price?: number; stockQuantity?: number } = {}
    if (priceTrim) {
      const price = Number(priceTrim)
      if (!Number.isFinite(price) || price <= 0) {
        pushToast({ message: 'Price must be a number greater than zero.', variant: 'error' })
        return
      }
      updates.price = price
    }
    if (stockTrim) {
      const stock = Number(stockTrim)
      if (!Number.isInteger(stock) || stock < 0) {
        pushToast({ message: 'Stock must be a whole number zero or greater.', variant: 'error' })
        return
      }
      updates.stockQuantity = stock
    }

    pushToast({ message: 'Bulk update started…', variant: 'info', duration: 2500 })
    void finishBulkAction('Price/stock updated', async () => {
      const result = await bulkUpdateProductPriceStock(selectedItems, updates)
      if (result.failed.length === 0) {
        setBulkPrice('')
        setBulkStock('')
      }
      return result
    })
  }

  async function finishBulkAction(
    actionLabel: string,
    run: () => Promise<{ succeeded: number; failed: BulkProductFailure[] }>,
  ) {
    setBulkBusy(true)
    setBulkFailures([])
    try {
      const result = await run()
      const { toast: message, variant } = formatBulkResultMessage(actionLabel, result)
      pushToast({ message, variant })
      setBulkFailures(result.failed)
      if (result.succeeded > 0) {
        setSelectedIds(() =>
          result.failed.length === 0 ? new Set() : new Set(result.failed.map((f) => f.id)),
        )
        await reload()
      }
    } finally {
      setBulkBusy(false)
    }
  }

  const hasActiveFilters =
    searchInput.trim() !== '' ||
    categorySlug !== '' ||
    statusFilter !== 'all' ||
    lowStockFilter

  const clearFilters = useCallback(() => {
    setSearchInput('')
    setQ('')
    setCategorySlug('')
    setStatusFilter('all')
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('lowStock')
      return next
    })
  }, [setSearchParams])

  const toggleLowStockFilter = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (next.get('lowStock') === '1') next.delete('lowStock')
      else next.set('lowStock', '1')
      return next
    })
  }, [setSearchParams])

  const showEmptyCatalog = !listLoading && listTotal === 0 && !hasActiveFilters
  const showEmptyFilters = !listLoading && rows.length === 0 && hasActiveFilters

  return (
    <Card>
      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete selected products?"
        variant="danger"
        confirmLabel="Delete products"
        description={
          <>
            Permanently delete{' '}
            <strong className="text-foreground">{selectedItems.length}</strong> product
            {selectedItems.length === 1 ? '' : 's'}? This cannot be undone.
          </>
        }
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={async () => {
          await finishBulkAction('Deleted', () => bulkDeleteProducts(selectedItems))
          setDeleteConfirmOpen(false)
        }}
      />
      <CardHeader
        title="Products"
        description="Manage your store catalog."
        action={
          <Link to="/products/new">
            <Button size="sm">Add product</Button>
          </Link>
        }
      />
      {listError ? <p className="mb-4 text-sm text-destructive">{listError}</p> : null}

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
        <div className="min-w-[200px] flex-1">
          <Input
            id="product-search"
            label="Search"
            placeholder="Name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="w-full min-w-[160px] sm:w-auto sm:min-w-[200px]">
          <label htmlFor="product-category" className="mb-1.5 block text-sm font-medium text-foreground">
            Category
          </label>
          <select
            id="product-category"
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
            value={categorySlug}
            onChange={(e) => setCategorySlug(e.target.value)}
          >
            <option value="">All categories</option>
            {categoryRows.map((c) => (
              <option key={`${c.source}-${c.slug}-${c.id ?? 'x'}`} value={c.slug}>
                {c.name} ({c.slug})
              </option>
            ))}
          </select>
        </div>
        <div className="w-full min-w-[140px] sm:w-auto sm:min-w-[180px]">
          <label htmlFor="product-status" className="mb-1.5 block text-sm font-medium text-foreground">
            Status
          </label>
          <select
            id="product-status"
            className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <div className="flex w-full items-end sm:w-auto">
          <Button
            type="button"
            variant={lowStockFilter ? 'primary' : 'secondary'}
            className="w-full sm:w-auto"
            onClick={toggleLowStockFilter}
          >
            Low stock (≤{lowStockThreshold})
          </Button>
        </div>
        <div className="flex w-full items-end sm:w-auto">
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:w-auto"
            disabled={!hasActiveFilters}
            onClick={clearFilters}
          >
            Clear filters
          </Button>
        </div>
      </div>

      <ProductBulkActionsBar
        selectedCount={selectedItems.length}
        busy={bulkBusy || listLoading}
        categoryRows={categoryRows}
        bulkCategorySlug={bulkCategorySlug}
        onBulkCategorySlugChange={setBulkCategorySlug}
        bulkPrice={bulkPrice}
        onBulkPriceChange={setBulkPrice}
        bulkStock={bulkStock}
        onBulkStockChange={setBulkStock}
        failures={bulkFailures}
        onPublish={() => void finishBulkAction('Published', () => bulkSetProductActive(selectedItems, true))}
        onUnpublish={() => void finishBulkAction('Unpublished', () => bulkSetProductActive(selectedItems, false))}
        onAssignCategory={() =>
          void finishBulkAction('Category assigned', () =>
            bulkAssignProductCategory(selectedItems, bulkCategorySlug),
          )
        }
        onApplyPriceStock={applyBulkPriceStock}
        onDelete={() => setDeleteConfirmOpen(true)}
        onClearSelection={clearSelection}
      />

      {listLoading ? (
        <SkeletonListPage toolbarFields={3} columns={9} rows={8} />
      ) : showEmptyCatalog ? (
        <EmptyState
          title="No products yet"
          description="Add your first product to start selling on your storefront."
          action={
            <Link to="/products/new">
              <Button type="button">Add your first product</Button>
            </Link>
          }
        />
      ) : showEmptyFilters ? (
        <EmptyState
          title="No products match your filters"
          description="Try a different search or clear filters to see your catalog."
          action={
            <Button type="button" variant="secondary" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/30 text-left text-muted-foreground">
                <tr>
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border"
                      checked={allOnPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected
                      }}
                      onChange={toggleAllOnPage}
                      aria-label="Select all products on this page"
                    />
                  </th>
                  <th className="px-4 py-3 font-medium">S/N</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Slug</th>
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">Stock</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {rows.map((item, rowIndex) => (
                  <tr key={item.id} className={selectedIds.has(item.id) ? 'bg-primary/5' : undefined}>
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleRow(item.id)}
                        aria-label={`Select ${item.name}`}
                      />
                    </td>
                    <td className="px-4 py-3 tabular-nums">{rowIndex + 1}</td>
                    <td className="px-4 py-3">{item.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{item.slug}</td>
                    <td className="px-4 py-3">{item.sku}</td>
                    <td className="px-4 py-3 tabular-nums">
                      <InlineNumberEdit
                        value={Number(item.price)}
                        min={0.01}
                        step={0.01}
                        inputMode="decimal"
                        ariaLabel={`Price for ${item.name}`}
                        disabled={bulkBusy || inlineSavingId === item.id}
                        formatDisplay={(n) =>
                          formatMoney(n, resolveCurrencyCode(item.currency))
                        }
                        onSave={(next) => saveInlineField(item.id, 'price', next)}
                      />
                    </td>
                    <td className="px-4 py-3 tabular-nums">
                      <div className="flex flex-wrap items-center gap-2">
                        <InlineNumberEdit
                          value={item.stockQuantity}
                          min={0}
                          step={1}
                          inputMode="numeric"
                          ariaLabel={`Stock for ${item.name}`}
                          disabled={bulkBusy || inlineSavingId === item.id}
                          formatDisplay={(n) => String(n)}
                          onSave={(next) => saveInlineField(item.id, 'stockQuantity', next)}
                        />
                        <StockLevelBadge
                          stockQuantity={item.stockQuantity}
                          threshold={lowStockThreshold}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.isActive ? (
                        <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                          Published
                        </span>
                      ) : (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/products/${item.id}`} className="text-primary hover:underline">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <LoadMoreFooter
            shown={rows.length}
            total={listTotal}
            hasMore={hasMore}
            loading={listLoading}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </>
      )}
    </Card>
  )
}
